import { useState } from 'react';
import {
  Button,
  ConfirmDialog,
  IconButton,
  Menu,
  MenuItem,
  MenuSeparator,
  MoreIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  cx,
} from '@/shared/ui';
import { SHELF_COLORS, type Shelf, type ShelfColor } from '@/features/library/services/shelfService';

export const ALL_SHELF_ID = '__all';

/* Not a shelf and never stored: derived from status, the way `All` is derived
   from the whole set. Putting it in `BUILTIN_SHELVES` would imply membership
   rows that nothing writes — the mistake Saved already had to be rescued from.
   It replaces staging's status filter pills, which the Iteration 2.0 `Filter`
   component drops in favour of shelves; the pills also let you find a failed
   generation in one click, and that part was worth keeping. */
export const ATTENTION_SHELF_ID = '__attention';

export type ShelfPanelProps = {
  shelves: Shelf[];
  counts: Record<string, number>;
  totalCount: number;
  /** Failed and awaiting-confirmation items. The row hides itself at zero. */
  attentionCount?: number;
  selectedId: string;
  onSelect: (shelfId: string) => void;
  onCreate: (name: string) => void;
  onRename: (shelfId: string, name: string) => void;
  onRecolor: (shelfId: string, color: ShelfColor) => void;
  onDelete: (shelfId: string) => void;
  /**
   * Controlled, so the library card's `Move to shelf › Start a new shelf` can
   * open this rail's name field instead of being a menu item that does nothing.
   */
  creating: boolean;
  onCreatingChange: (creating: boolean) => void;
};

/** The `Browse by` rail: All, the user's shelves, then Saved and Favorites. */
export function ShelfPanel({
  shelves,
  counts,
  totalCount,
  attentionCount = 0,
  selectedId,
  onSelect,
  onCreate,
  onRename,
  onRecolor,
  onDelete,
  creating,
  onCreatingChange,
}: ShelfPanelProps) {
  const [draftName, setDraftName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Shelf | null>(null);

  const commitCreate = () => {
    const name = draftName.trim();
    if (name) onCreate(name);
    setDraftName('');
    onCreatingChange(false);
  };

  const commitRename = (shelfId: string) => {
    const name = renameValue.trim();
    if (name) onRename(shelfId, name);
    setRenamingId(null);
    setRenameValue('');
  };

  return (
    <aside className="shelves" aria-label="Browse by shelf">
      <h2 className="shelves__title">Browse by</h2>

      <ul className="shelves__list">
        <li>
          <button
            type="button"
            className={cx('shelf-row', selectedId === ALL_SHELF_ID && 'is-selected')}
            aria-current={selectedId === ALL_SHELF_ID ? 'true' : undefined}
            onClick={() => onSelect(ALL_SHELF_ID)}
          >
            <span className="shelf-row__name">All</span>
            <span className="shelf-row__count">{totalCount}</span>
          </button>
        </li>

        {/* Absent rather than present at zero: a healthy library should not grow
            a permanent row saying nothing is wrong. */}
        {attentionCount > 0 ? (
          <li>
            <button
              type="button"
              className={cx(
                'shelf-row',
                'shelf-row--attention',
                selectedId === ATTENTION_SHELF_ID && 'is-selected',
              )}
              aria-current={selectedId === ATTENTION_SHELF_ID ? 'true' : undefined}
              onClick={() => onSelect(ATTENTION_SHELF_ID)}
            >
              <span className="shelf-row__dot shelf-row__dot--attention" aria-hidden />
              <span className="shelf-row__name">Needs attention</span>
              <span className="shelf-row__count">{attentionCount}</span>
            </button>
          </li>
        ) : null}

        {shelves.map((shelf) => {
          const selected = shelf.id === selectedId;
          const isRenaming = renamingId === shelf.id;

          return (
            <li key={shelf.id} className="shelves__item">
              {isRenaming ? (
                <input
                  className="shelf-row__input"
                  value={renameValue}
                  autoFocus
                  aria-label={`Rename ${shelf.name}`}
                  onChange={(event) => setRenameValue(event.target.value)}
                  onBlur={() => commitRename(shelf.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') commitRename(shelf.id);
                    if (event.key === 'Escape') {
                      setRenamingId(null);
                      setRenameValue('');
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className={cx('shelf-row', selected && 'is-selected')}
                  aria-current={selected ? 'true' : undefined}
                  onClick={() => onSelect(shelf.id)}
                >
                  <span className={cx('shelf-row__dot', `shelf-row__dot--${shelf.color}`)} aria-hidden />
                  <span className="shelf-row__name">{shelf.name}</span>
                  <span className="shelf-row__count">{counts[shelf.id] ?? 0}</span>
                </button>
              )}

              {/* Built-ins have no editable identity, so they get no menu. */}
              {!shelf.builtin && !isRenaming ? (
                <Menu
                  label={`${shelf.name} actions`}
                  placement="bottom-end"
                  className="shelves__menu"
                  trigger={
                    <IconButton label={`${shelf.name} actions`} size="sm" variant="ghost">
                      <MoreIcon size={16} />
                    </IconButton>
                  }
                >
                  <MenuItem
                    icon={<PencilIcon size={16} />}
                    onSelect={() => {
                      setRenameValue(shelf.name);
                      setRenamingId(shelf.id);
                    }}
                  >
                    Rename
                  </MenuItem>

                  {/* A radio group, which is a legal child of a menu and is what
                      six mutually-exclusive colours actually are — `aria-pressed`
                      described six independent toggles. The click is stopped from
                      bubbling because Menu closes on any click inside it: picking
                      a colour tore the menu down, so comparing two greens meant
                      reopening the menu between them. Recolouring is the one
                      action here you want to try more than once. */}
                  <div
                    className="shelf-colors"
                    role="group"
                    aria-label="Shelf colour"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <span className="shelf-colors__label">Color</span>
                    <div className="shelf-colors__swatches">
                      {SHELF_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          role="menuitemradio"
                          tabIndex={-1}
                          aria-label={color}
                          aria-checked={shelf.color === color}
                          className={cx(
                            'shelf-colors__swatch',
                            `shelf-colors__swatch--${color}`,
                            shelf.color === color && 'is-active',
                          )}
                          onClick={() => onRecolor(shelf.id, color)}
                        />
                      ))}
                    </div>
                  </div>

                  <MenuSeparator />
                  <MenuItem
                    icon={<TrashIcon size={16} />}
                    tone="danger"
                    onSelect={() => setPendingDelete(shelf)}
                  >
                    Delete
                  </MenuItem>
                </Menu>
              ) : null}
            </li>
          );
        })}
      </ul>

      {creating ? (
        <input
          className="shelf-row__input"
          value={draftName}
          autoFocus
          placeholder="Shelf name"
          aria-label="New shelf name"
          onChange={(event) => setDraftName(event.target.value)}
          onBlur={commitCreate}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commitCreate();
            if (event.key === 'Escape') {
              setDraftName('');
              onCreatingChange(false);
            }
          }}
        />
      ) : (
        <Button
          variant="secondary"
          size="sm"
          block
          iconLeft={<PlusIcon size={15} />}
          onClick={() => onCreatingChange(true)}
        >
          New shelf
        </Button>
      )}

      {/* Deleting a shelf was the one destructive action on this branch that
          happened on a single click, with no confirmation and no undo. It is
          also a sibling of the list rather than a child of the menu: a
          ConfirmDialog renders through a portal, but React routes synthetic
          events along the component tree, so inside the Menu its button clicks
          would bubble into the menu's own close-on-click handler.

          "Delete shelf" / "Keep it" rather than Confirm / Cancel, and the copy
          says what survives — losing a shelf is not losing the videos on it. */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Delete ${pendingDelete?.name ?? 'this shelf'}?`}
        description="The shelf goes away. The videos on it stay in your library."
        confirmLabel="Delete shelf"
        cancelLabel="Keep it"
        onConfirm={() => {
          if (pendingDelete) onDelete(pendingDelete.id);
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </aside>
  );
}
